import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Backdrop, Box, Button, Checkbox, Container, Dialog, DialogContent,
  Divider, FormControlLabel, LinearProgress, Paper, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { AccessTime, CheckCircle, CreditCard, DesignServices, Language, Launch, OpenInNew, ScheduleRounded, Tune } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import LoadingSpinner from '../../../components/global/LoadingSpinner';
import {
  useActivateMyTenant, useCompleteOnboardingIntroduction, useStartOnboardingStep,
  useTenantOnboardingStatus,
} from '../../../sitePublishing/hooks';
import { OnboardingStepId } from '../../../sitePublishing/types';
import EditWorkHoursPage from '../EditWorkhours/EditWorkHoursPage';
import ManageEmployeesPage from '../ManageEmployeesPage';
import ManageSubscriptionPage from '../ManageSubscription/ManageSubscriptionPage';
import ServicesScreen from '../ServicesScreen';
import SitePublishingWorkspace from '../SitePublishing/SitePublishingWorkspace';
import { FirstServiceWizard, GuidedAvailability, ModeQuestionnaire, SiteExperienceChoice } from './GuidedOnboardingSteps';
import ScheduifyWordmark from '../../../components/brand/ScheduifyWordmark';
import { createControlPlaneTenantUrl } from '../../../tenancy/tenantUrls';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { convertTo12Hour, extractTimesFromBitString } from '../../../time/timeUtils';
import { DAYJS_DAYS_OF_WEEK } from '../../../constants';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../../../tenancy/TenantContext';
import { CompletedStepCard, launchActionButtonSx, ReviewSummary } from './OnboardingPrimitives';

const stepIcons: Record<OnboardingStepId, React.ElementType> = {
  mode: Tune, availability: AccessTime, service: DesignServices,
  site: Language, payment: CreditCard, goLive: Launch,
};

// Shown in turn while launch runs, so the wait never looks frozen.
const LAUNCH_MESSAGES = [
  'Finalizing your website and booking portal.',
  'Turning on online booking.',
  'Almost there. Keep this page open.',
];

const LaunchProgress: React.FC = () => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (index >= LAUNCH_MESSAGES.length - 1) return undefined;
    const timer = window.setTimeout(() => setIndex((value) => value + 1), 5000);
    return () => window.clearTimeout(timer);
  }, [index]);
  return <Typography key={index} color="text.secondary" role="status" aria-live="polite">{LAUNCH_MESSAGES[index]}</Typography>;
};

const OnboardingPage: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();
  const statusQuery = useTenantOnboardingStatus();
  const activationMutation = useActivateMyTenant();
  const introMutation = useCompleteOnboardingIntroduction();
  const startMutation = useStartOnboardingStep();
  const initializedTenantRef = useRef<string>();
  const previousCompletionRef = useRef<Record<string, boolean>>({});
  const [selectedStep, setSelectedStep] = useState<OnboardingStepId>('mode');
  const [customizeAvailability, setCustomizeAvailability] = useState(false);
  const [introPage, setIntroPage] = useState(0);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const status = statusQuery.data;
    if (!status) return;
    if (initializedTenantRef.current !== status.tenantId) {
      initializedTenantRef.current = status.tenantId;
      setSelectedStep(status.steps.find((step) => step.current)?.id ?? 'goLive');
      previousCompletionRef.current = Object.fromEntries(status.steps.map((step) => [step.id, step.complete]));
      return;
    }
    const selected = status.steps.find((step) => step.id === selectedStep);
    if (selected?.complete && previousCompletionRef.current[selectedStep] === false) {
      setSelectedStep(selectedStep === 'payment' ? 'goLive' : status.steps.find((step) => step.current)?.id ?? selectedStep);
    }
    previousCompletionRef.current = Object.fromEntries(status.steps.map((step) => [step.id, step.complete]));
  }, [selectedStep, statusQuery.data]);

  if (statusQuery.isLoading) return <LoadingSpinner label="Loading your setup" />;
  if (statusQuery.isError || !statusQuery.data) return <Container maxWidth="md"><Alert severity="info">The launch checklist is not available for this tenant.</Alert></Container>;
  const status = statusQuery.data;
  const selectedMetadata = status.steps.find((step) => step.id === selectedStep);
  const launchPrerequisitesComplete = Boolean(status.operatingMode) &&
    status.gates.availability.complete && status.gates.service.complete;
  const canVisitPayment = launchPrerequisitesComplete;
  const canVisitGoLive = launchPrerequisitesComplete && status.gates.payment.complete;
  const visibleSteps = status.steps.filter((step) => step.id !== 'goLive' || status.gates.payment.complete);
  const completedSteps = status.steps.filter((step) => step.complete && step.id !== 'goLive').length;
  const checklistProgress = Math.round((completedSteps / Math.max(status.requiredCount, 1)) * 100);
  const isStepAccessible = (stepId: OnboardingStepId, locked: boolean) =>
    !locked || (stepId === 'payment' && canVisitPayment) || (stepId === 'goLive' && canVisitGoLive);
  const selectStep = (stepId: OnboardingStepId) => {
    const step = status.steps.find((entry) => entry.id === stepId);
    if (!step || !isStepAccessible(step.id, step.locked)) return;
    if (stepId !== 'availability') setCustomizeAvailability(false);
    setSelectedStep(stepId);
    if (!step.started && !step.locked) startMutation.mutate(stepId);
  };
  const openDashboard = () => window.location.assign(createControlPlaneTenantUrl('/admin/schedule-hub', status.tenantId));

  if (status.lifecycleState === 'active') return <Box sx={{
    minHeight: 'calc(100dvh - 64px)',
    px: { xs: 2, sm: 3 },
    py: { xs: 4, md: 6 },
    border: 0,
    outline: 0,
    overflow: 'hidden',
    display: 'grid',
    placeItems: 'center',
    position: 'relative',
    fontFamily: 'Quicksand, sans-serif',
    background: 'radial-gradient(circle at 12% 12%, rgba(255,255,255,.22), transparent 26%), radial-gradient(circle at 88% 88%, rgba(105,211,255,.35), transparent 30%), linear-gradient(145deg, #0877e8 0%, #1768d8 52%, #0d50b8 100%)',
  }}>
    <Box aria-hidden="true" sx={{ position: 'absolute', width: 240, height: 240, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.08)', top: -100, right: -85 }} />
    <Box aria-hidden="true" sx={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.07)', bottom: -80, left: -55 }} />

    <Box sx={{ width: '100%', maxWidth: 720, position: 'relative' }}>
      <Paper elevation={0} sx={{
        width: '100%',
        p: { xs: '44px 24px 34px', sm: 7, md: 8 },
        border: 'none',
        outline: 'none',
        borderRadius: { xs: 5, sm: 7 },
        textAlign: 'center',
        boxShadow: '0 28px 80px rgba(0,35,93,.28)',
      }}>
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 28, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20, mass: 0.9 }}
        >
          <Typography variant="h3" component="h1" fontWeight={900} sx={{
            fontSize: { xs: '2rem', sm: '3rem' }, lineHeight: 1.12, letterSpacing: '-.035em',
          }}>
            Your <Box component="span" sx={{ fontFamily: 'Shrikhand, cursive', color: '#1976d2', fontWeight: 400, letterSpacing: 0 }}>Scheduify</Box> site is ready
          </Typography>
        </motion.div>
        <Typography color="text.secondary" sx={{ mt: 2, mb: 3.5, fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.6 }}>
          You're live. Customers can now visit your website and book.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          {status.publicUrl ? <Button variant="contained" size="large" href={status.publicUrl} target="_blank" rel="noopener noreferrer"
            endIcon={<OpenInNew />} sx={{ ...launchActionButtonSx, minHeight: 54, px: 4 }}>
            View my website
          </Button> : null}
          <Button variant="outlined" size="large" onClick={openDashboard} sx={{ ...launchActionButtonSx, minHeight: 54, px: 4, borderWidth: 2,
            '&:hover': { borderWidth: 2, boxShadow: 'none' } }}>
            Go to my dashboard
          </Button>
        </Stack>
      </Paper>
    </Box>
  </Box>;

  return <Box sx={{ minHeight: 'calc(100vh - 64px)', pb: { xs: 15, sm: 8 }, fontFamily: 'Quicksand, sans-serif',
    background: 'radial-gradient(circle at 8% 0%, rgba(81,173,255,.13), transparent 30%), radial-gradient(circle at 94% 18%, rgba(116,212,255,.12), transparent 26%), #f7fafe',
    '& .MuiButton-containedPrimary': { bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } },
    '& .MuiTabs-indicator': { bgcolor: '#1976d2' } }}>
    <Dialog open={!status.introductionComplete} maxWidth="sm" fullWidth slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(10px)', bgcolor: 'rgba(7,18,35,.64)' } } }}
      PaperProps={{ sx: { m: { xs: 2, sm: 4 }, borderRadius: { xs: 4, sm: 5 }, overflow: 'hidden', boxShadow: '0 32px 90px rgba(0,0,0,.28)' } }}>
      <DialogContent sx={{ p: { xs: 3, md: 5 }, position: 'relative',
        '&::before': { content: '""', position: 'absolute', inset: '0 0 auto', height: 6, background: 'linear-gradient(90deg,#1976d2,#74d4ff)' } }}>
        <Stack spacing={3.25}>
          <ScheduifyWordmark variant="h4" />
          <Box sx={{ width: 72, height: 72, borderRadius: 4, display: 'grid', placeItems: 'center', bgcolor: '#eaf4ff', fontSize: 36 }} aria-hidden="true">
            {introPage === 0 ? '🚀' : '✨'}
          </Box>
          <Typography variant="h4" fontWeight={900} sx={{ lineHeight: 1.15, letterSpacing: '-.025em' }}>{introPage === 0 ? 'Let’s get you ready to launch' : 'You’re never doing this alone'}</Typography>
          <Typography color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.65 }}>{introPage === 0 ? 'We’ll guide you through a few quick wins—your setup, availability, first service, website, and trial.' : 'Most people finish in one sitting. If you get stuck, our team is available at support@scheduify.com.'}</Typography>
          <LinearProgress variant="determinate" value={introPage === 0 ? 50 : 100} sx={{ height: 8, borderRadius: 99, bgcolor: '#e7edf5', '& .MuiLinearProgress-bar': { borderRadius: 99 } }} />
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" size="large" fullWidth disabled={introMutation.isPending}
              sx={{ ...launchActionButtonSx, minHeight: 54 }}
              onClick={() => introPage === 0 ? setIntroPage(1) : introMutation.mutate()}>{introPage === 0 ? 'Show me how' : 'Start my setup'}</Button>
          </Stack>
          {introMutation.isError ? <Alert severity="error">{introMutation.error.message}</Alert> : null}
        </Stack>
      </DialogContent>
    </Dialog>
    <Container maxWidth="lg" sx={{ pt: { xs: 1.5, md: 4 }, px: { xs: 1.5, sm: 3 } }}>
      <Stack spacing={{ xs: 1.25, md: 3.5 }}>
        <Box sx={{ display: { xs: 'none', sm: 'block' }, px: 1 }}>
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="overline" color="primary.main" fontWeight={900} letterSpacing=".11em">YOUR LAUNCH PATH</Typography>
              <Typography variant="h4" component="h1" fontWeight={900} sx={{ fontSize: { xs: '1.55rem', sm: '2rem' }, letterSpacing: '-.025em' }}>
                Let’s build {status.business.displayName}
              </Typography>
            </Box>
            <Typography fontWeight={900} color="primary.main" sx={{ fontSize: { xs: '.9rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>{checklistProgress}% ready</Typography>
          </Stack>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: { xs: 3, sm: 4 }, overflow: 'hidden', border: '1px solid rgba(25,118,210,.14)',
          boxShadow: '0 10px 32px rgba(15,23,42,.07)', position: { xs: 'sticky', sm: 'static' }, top: { xs: 0, sm: 'auto' }, zIndex: 10,
          bgcolor: 'rgba(255,255,255,.96)', backdropFilter: 'blur(14px)' }}>
          <LinearProgress variant="determinate" value={checklistProgress} aria-label={`${checklistProgress}% of launch checklist complete`}
            sx={{ height: 5, bgcolor: '#e8eef6', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#1976d2,#55b4ff)' } }} />
          <Tabs value={selectedStep} onChange={(_, value: OnboardingStepId) => selectStep(value)} variant="fullWidth" aria-label="Launch setup steps"
            sx={{ px: { xs: .5, sm: 1 }, '& .MuiTabs-indicator': { height: 4, borderRadius: '4px 4px 0 0' },
              '& .MuiTab-root': { minWidth: 0, minHeight: { xs: 52, sm: 82 }, textTransform: 'none', fontWeight: 850, px: { xs: .25, sm: 1 }, py: { xs: .65, sm: 1.5 },
                transition: 'background-color 160ms ease, color 160ms ease', '&.Mui-selected': { bgcolor: 'rgba(25,118,210,.055)' } } }}>
            {visibleSteps.map((step) => {
              const Icon = step.complete ? CheckCircle : stepIcons[step.id];
              const accessible = isStepAccessible(step.id, step.locked);
              return <Tab key={step.id} value={step.id} disabled={!accessible}
                icon={<Box sx={{ width: { xs: 30, sm: 40 }, height: { xs: 30, sm: 40 }, borderRadius: '50%', display: 'grid', placeItems: 'center',
                  bgcolor: step.complete ? '#e8f7ef' : selectedStep === step.id ? '#e8f2ff' : '#f0f3f7' }}>
                  <Icon sx={{ color: step.complete ? 'success.main' : selectedStep === step.id ? 'primary.main' : 'text.secondary', fontSize: { xs: 18, sm: 23 } }} />
                </Box>}
                iconPosition="top" label={<Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>{step.label}</Box>}
                aria-label={`${step.label}: ${step.complete ? 'complete' : accessible ? 'available' : 'locked'}`} />;
            })}
          </Tabs>
        </Paper>

        <Box role="tabpanel" aria-label={selectedMetadata?.label} sx={{ minWidth: 0, width: '100%' }}>
          {selectedStep === 'mode' ? status.operatingMode ? <CompletedStepCard eyebrow="SETUP COMPLETE"
            title={status.operatingMode === 'team' ? 'Team scheduling is ready' : 'Front Desk is ready'}
            detail={status.operatingMode === 'team'
              ? 'Your scheduling setup is confirmed. You will choose your team size when you start your trial.'
              : 'Your scheduling setup and Front Desk plan are confirmed.'}>
            <Box sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 3.5, bgcolor: 'rgba(25,118,210,.045)',
              border: '1px solid rgba(25,118,210,.14)' }}>
              <ReviewSummary rows={[
                { label: 'Business', value: status.business.displayName },
                { label: 'Timezone', value: status.business.timezone },
                { label: 'Management', value: status.managementStyle === 'individual_logins' ? 'Individual logins' : 'Central manager' },
                { label: 'Customer booking', value: status.bookingChoice === 'specific_person' ? 'Choose an employee' : 'Automatic assignment' },
                { label: 'Customer terminology', value: status.resourcePlural ?? (status.bookingSubject === 'resources' ? 'Resources' : 'Employees') },
              ]} />
            </Box>
          </CompletedStepCard> : <ModeQuestionnaire status={status} /> : null}
          {selectedStep === 'availability' ? status.gates.availability.complete && !customizeAvailability ? <AvailabilityReview status={status} onCustomize={() => setCustomizeAvailability(true)} /> : status.operatingMode === 'front_desk' && status.resourcesProvisioned ? <Stack spacing={2}>
            {!status.gates.availability.complete ? <Alert severity="warning">{status.incompleteResourceNames?.length ? `Needs valid availability: ${status.incompleteResourceNames.join(', ')}.` : 'Complete availability for every resource.'}</Alert> : null}<ManageEmployeesPage />
          </Stack> : status.gates.availability.complete ? <EditWorkHoursPage requireBookableAvailability embedded /> : <GuidedAvailability status={status} /> : null}
          {selectedStep === 'service' ? status.gates.service.complete ? <ServicesScreen /> : <FirstServiceWizard status={status} /> : null}
          {selectedStep === 'site' ? status.siteExperience === 'full_site' ? <SitePublishingWorkspace /> : <SiteExperienceChoice status={status} /> : null}
          {selectedStep === 'payment' ? <ManageSubscriptionPage onboardingMode={status.operatingMode} onboardingPlanKey={status.selectedPlanKey}
            onboardingCapacity={status.operatingMode === 'front_desk' ? status.resourceCount : status.teamSize}
            onboardingPaymentComplete={status.gates.payment.complete} onContinue={() => selectStep('goLive')} /> : null}
          {selectedStep === 'goLive' ? activationMutation.isPending ? <Paper elevation={0} sx={{ p: { xs: 4, md: 7 }, textAlign: 'center' }}>
            <Stack spacing={3} alignItems="center" sx={{ maxWidth: 520, mx: 'auto' }}>
              <ScheduifyWordmark variant="h3" />
              <Typography variant="h5" fontWeight={850}>Publishing your site</Typography>
              <LinearProgress sx={{ width: '100%', height: 8, borderRadius: 999 }} />
              <LaunchProgress />
            </Stack>
          </Paper> : <Paper elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
            <Stack spacing={3} alignItems="flex-start">
              <Typography variant="h4" component="h2" fontWeight={900}>Ready to <Box component="span" sx={{ fontFamily: 'Shrikhand, cursive', color: '#1976d2', fontWeight: 400 }}>Scheduify</Box>?</Typography>
              <Typography color="text.secondary">Launch your website and begin accepting customer bookings.</Typography>
              {!status.gates.sitePublished.complete ? <Alert severity="warning" action={<Button color="inherit" sx={{ fontWeight: 900 }} onClick={() => selectStep('site')}>Build your site</Button>}>
                Your booking site setup is not complete. Finish or confirm it before launching.
              </Alert> : null}
              <FormControlLabel control={<Checkbox checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />} label={<span>I agree to the <a href="https://scheduify.com/terms/" target="_blank" rel="noreferrer">Terms of Service and Privacy Policy</a>.</span>} />
              <Button variant="contained" size="large" sx={launchActionButtonSx} onClick={() => activationMutation.mutate()} disabled={!accepted || !status.canActivate || activationMutation.isPending}>{activationMutation.isPending ? 'Launching...' : 'Launch'}</Button>
              {activationMutation.isError ? <Alert severity="error">{activationMutation.error.message}</Alert> : null}
            </Stack>
          </Paper> : null}
        </Box>
        {startMutation.isError ? <Alert severity="error">{startMutation.error.message}</Alert> : null}
      </Stack>
    </Container>
  </Box>;
};

const AvailabilityReview: React.FC<{ status: import('../../../sitePublishing/types').TenantOnboardingStatus; onCustomize: () => void }> = ({ status, onCustomize }) => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const { currentEmployeeQuery, workHoursQuery, employeesQuery } = useAdminData();
  const workHours = workHoursQuery.data;
  const recurringBreaks = currentEmployeeQuery.data?.recurringBreaks ?? [];
  const preferredDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const resources = (employeesQuery.data ?? []).filter((employee) => employee.isOrchestrated && employee.active !== false);
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['employeeWorkHours', tenantId] });
    if (status.operatingMode === 'front_desk') {
      void queryClient.invalidateQueries({ queryKey: ['employees', tenantId], exact: true });
    }
  }, [queryClient, status.operatingMode, tenantId]);
  const resourceHoursQueries = useQueries({ queries: resources.map((employee) => ({
    queryKey: ['employeeWorkHours', tenantId, employee.id],
    queryFn: async () => (await import('../../../firebase/firebase-delegate')).getEmployeeWorkHours(employee.id, tenantId),
    staleTime: 5_000,
  })) });

  return <CompletedStepCard eyebrow="AVAILABILITY COMPLETE" title="Your availability is ready"
    detail="These are the hours customers can book. You can adjust them at any time.">
    <Stack spacing={2.5}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
        <ScheduleRounded color="primary" sx={{ fontSize: 21 }} />
        <Typography variant="body2" fontWeight={750}>{status.business.timezone}</Typography>
      </Stack>
      {status.operatingMode === 'front_desk' ? <Stack spacing={2}>
        {resources.map((employee, index) => <Paper key={employee.id} variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3.5,
          borderColor: 'rgba(25,118,210,.16)', bgcolor: 'rgba(255,255,255,.78)' }}>
          <Typography fontWeight={900} sx={{ mb: 1.25 }}>{`${employee.firstName} ${employee.lastName}`.trim()}</Typography>
          {resourceHoursQueries[index]?.data ? <HoursRows workHours={resourceHoursQueries[index].data!} recurringBreaks={employee.recurringBreaks ?? []} days={preferredDays} /> : <LoadingSpinner />}
        </Paper>)}
      </Stack> : workHours ? <Paper variant="outlined" sx={{ px: { xs: 2, sm: 2.5 }, borderRadius: 3.5,
        borderColor: 'rgba(25,118,210,.16)', bgcolor: 'rgba(255,255,255,.78)' }}>
        <HoursRows workHours={workHours} recurringBreaks={recurringBreaks} days={preferredDays} />
      </Paper> : <LoadingSpinner />}
      <Button variant="outlined" sx={{ ...launchActionButtonSx, alignSelf: { xs: 'stretch', sm: 'flex-start' }, borderWidth: 2,
        '&:hover': { borderWidth: 2, boxShadow: 'none' } }} onClick={onCustomize}>Edit work hours</Button>
    </Stack>
  </CompletedStepCard>;
};

const HoursRows: React.FC<{ workHours: import('../../../types').WorkHours; recurringBreaks: import('../../../types').RecurringBreak[]; days: readonly (keyof import('../../../sitePublishing/types').PublicBusinessHours)[] }> = ({ workHours, recurringBreaks, days }) => <Stack divider={<Divider flexItem />}>
  {days.map((day) => {
    const parsed = extractTimesFromBitString(workHours[day]);
    const breaks = recurringBreaks.filter((definition) => DAYJS_DAYS_OF_WEEK[definition.weekday] === day);
    return <Stack key={day} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={.5} sx={{ py: 1.15 }}>
      <Typography fontWeight={750} textTransform="capitalize">{day}</Typography>
      <Box sx={{ textAlign: { sm: 'right' } }}>
        <Typography color={parsed.isDayOff ? 'text.secondary' : 'text.primary'}>{parsed.isDayOff ? 'Closed' : `${parsed.startTime.format('h:mm A')} – ${parsed.endTime.format('h:mm A')}`}</Typography>
        {breaks.map((definition) => <Typography key={definition.id} variant="caption" color="text.secondary" display="block">Break: {convertTo12Hour(definition.startTime)} – {convertTo12Hour(definition.endTime)}</Typography>)}
      </Box>
    </Stack>;
  })}
</Stack>;

export default OnboardingPage;
